import {useAccountInformation, useMediaQueries} from 'presentation/pages';
import {translations} from 'presentation/translations-new';
import {HEADER_TITLES} from '../../constants/headerTitles';
import {useQueryParamsSync} from 'presentation/pages/private/booking/hooks/useQueryParamsSync';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useBookingSummary} from 'presentation/modules/booking/shared/hooks/useBookingSummary';
import {getTelehealthStepPosition} from 'presentation/modules/booking/telehealth/utils/getTelehealthStepPosition';
import {FeaturesEnum, HealthBookingEnum, HomeEnum, QueryParamsEnum} from 'presentation/constants';
import {useHideStepBarStore} from 'presentation/modules/booking/shared/store/useHideStepBarStore';
import {TELEHEALTH_LABELS} from '../../constants/stepLabels';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {SEARCH_BAR_PLACEHOLDER} from '../../constants/searchBarPlaceholder';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {useNavigate} from 'react-router-dom';
import {useBackButtonBooking} from 'presentation/modules/booking/shared/hooks/useBackButtonBooking';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';
import {ONLY_TELEMEDICINE_SPECIALTIES_CODE} from 'presentation/modules/booking/shared/constants/onlyTelemedicineSpecialties';
import {getHealthStepPosition} from 'presentation/modules/booking/health/utils/getHealthStepPosition';
import {TypeLocalNameEnum} from 'domain/usecases';

const {bookTelehealth} = translations['pt-br'].privateFlow.services;
const {searchSpecialty} = translations['pt-br'].privateFlow.bookingFlow;

export function useTelehealthBooking() {
	const stepsVisited = useRef(new Set<number>());
	const {isMobile, isLaptop, isDesktop} = useMediaQueries();
	const [isFirstVisit, setIsFirstVisit] = useState(true);
	const hideStepBar = useHideStepBarStore(state => state.hideStepBar);
	const hideSearchBar = useHideSearchBarStore(state => state.hideSearchBar);
	const setHideSearchBar = useHideSearchBarStore(state => state.setHideSearchBar);
	const setHideStepBar = useHideStepBarStore(state => state.setHideStepBar);
	const setRegisterLater = useFacialBiometricsStore(state => state.setRegisterLater);
	const {isEnabled: ourDoctorFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NOSSO_MEDICO,
	);
	const {isEnabled: isOnlyTelemedicineSpecialtyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SPECIALTY_ONLY_TELEMEDICINE_CHIP,
	);
	const {isEnabled: isNewResumePageEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_RESUME_BOOKING_PAGE,
	);
	const navigate = useNavigate();
	const {
		activeBookingFlowStep,
		clearSearch,
		setBookingData,
		setSearchParams,
		setActiveBookingFlowStep,
		bookingData,
	} = useQueryParamsSync({isFirstVisit});
	const {
		handleChangeModals,
		openDrawerState,
		setOpenDrawerState,
		abandonFlowState,
		setAbandonFlowState,
	} = useBookingSummary();

	const {isOurDoctorPlan} = useAccountInformation();
	const {SPECIALITY_OR_EXAM_STEP, PROFESSIONAL_STEP, REVIEW_STEP} = getTelehealthStepPosition();
	useBackButtonBooking({flowType: BookingTypeEnum.TELEHEALTH});

	const shouldBacktoHealthFlow =
		activeBookingFlowStep === PROFESSIONAL_STEP &&
		ONLY_TELEMEDICINE_SPECIALTIES_CODE.includes(bookingData.specialtyCode) &&
		bookingData.initialBookingLocationData?.bookingType === BookingTypeEnum.HEALTH &&
		isOnlyTelemedicineSpecialtyEnabled;

	const headerTitle = isMobile ? HEADER_TITLES[activeBookingFlowStep] : bookTelehealth;

	const shouldHideBackBtn =
		bookingData.isRebooking &&
		activeBookingFlowStep === PROFESSIONAL_STEP &&
		!shouldBacktoHealthFlow;

	const stepLabels = TELEHEALTH_LABELS;
	const isLastStep = activeBookingFlowStep === stepLabels.length - 1;

	const shouldHideSearchBar = hideSearchBar || isLastStep;

	const searchBarPlaceHolder = SEARCH_BAR_PLACEHOLDER[activeBookingFlowStep];

	const shouldShowOurDoctorAlert =
		ourDoctorFlagIsEnabled &&
		isOurDoctorPlan &&
		activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP;

	const searchBarTagId =
		SEARCH_BAR_PLACEHOLDER[activeBookingFlowStep] === searchSpecialty
			? AppointmentBookingEnum.CLICK_BUSCAR_ESPECIALIDADE
			: undefined;

	const showSwipeableDrawerBookingSummary = !isLastStep && isLaptop;

	const shouldUseNewResumeLayout = isNewResumePageEnabled && isLastStep;

	const handleClose = () => {
		setAbandonFlowState(true);
	};

	const handleBackToHealthFlow = () => {
		const {SPECIALITY_OR_EXAM_STEP} = getHealthStepPosition({
			isManualLocation:
				bookingData.initialBookingLocationData?.typeLocalName !==
				TypeLocalNameEnum.COORDENADAS,
		});

		setBookingData(prev => ({
			...prev,
			unitCode: '',
			unitName: '',
			unitAddress: '',
			ufCode: bookingData.initialBookingLocationData?.ufCode,
			ufName: bookingData.initialBookingLocationData?.ufName,
			cityName: bookingData.initialBookingLocationData?.cityName,
			coordinates: bookingData.initialBookingLocationData?.coordinates,
			bookingType: BookingTypeEnum.HEALTH,
			hourAt: '',
			dateAt: '',
			professionalCode: '',
			professionalName: '',
			specialtyCode: '',
			subSpecialtyCode: '',
			specialtyName: '',
			isManualLocation:
				bookingData.initialBookingLocationData?.typeLocalName === TypeLocalNameEnum.CIDADE,
			typeLocalName:
				bookingData.initialBookingLocationData?.typeLocalName || TypeLocalNameEnum.CIDADE,
			initialBookingLocationData: undefined,
			isRebooking: false,
		}));
		setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
		navigate(HealthBookingEnum.HEALTH_BOOKING_FLOW);
	};

	const handleBack = () => {
		clearSearch();

		if (activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			handleClose();
			return;
		}

		if (activeBookingFlowStep === PROFESSIONAL_STEP) {
			if (shouldBacktoHealthFlow) {
				handleBackToHealthFlow();
				return;
			}

			setBookingData(prev => ({
				...prev,
				isRebooking: false,
				unitCode: '',
				unitAddress: '',
				unitName: '',
				dateAt: '',
				multipleBookings: false,
				isScheduleScript: false,
			}));
		}

		if (activeBookingFlowStep === REVIEW_STEP) {
			setBookingData(prev => ({
				...prev,
				hourAt: '',
				dateAt: '',
				professionalCode: '',
				professionalName: '',
				initialBookingLocationData: undefined,
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.SHIFT);
					prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);

					return prev;
				},
				{
					replace: true,
				},
			);
		}

		setActiveBookingFlowStep(value => value - 1);
	};

	const handleConfirmExitFlow = useCallback(() => {
		setAbandonFlowState(false);
		navigate(HomeEnum.HOME_PAGE);
	}, []);

	const handleNext = () => {
		if (activeBookingFlowStep === stepLabels.length) {
			return;
		}
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.SEARCH_QUERY);
			return prev;
		});

		setActiveBookingFlowStep(value => value + 1);
	};

	useEffect(() => {
		document.getElementById('content-container')?.scrollTo({top: 0, behavior: 'auto'});
		window.scrollTo({top: 0, left: 0, behavior: 'auto'});
		const isFirstVisitValue = !stepsVisited.current.has(activeBookingFlowStep);
		setIsFirstVisit(isFirstVisitValue);
		stepsVisited.current.add(activeBookingFlowStep);
	}, [activeBookingFlowStep]);

	useEffect(() => {
		setHideSearchBar(false);
		setHideStepBar(false);
		clearSearch();
	}, [activeBookingFlowStep]);

	useEffect(() => {
		return () => {
			setRegisterLater(false);
		};
	}, []);

	return {
		headerTitle,
		handleClose,
		handleBack,
		shouldHideBackBtn,
		isMobile,
		hideStepBar,
		activeBookingFlowStep,
		stepLabels,
		shouldHideSearchBar,
		shouldShowOurDoctorAlert,
		searchBarTagId,
		searchBarPlaceHolder,
		setActiveBookingFlowStep,
		handleConfirmExitFlow,
		abandonFlowState,
		setAbandonFlowState,
		isLastStep,
		showSwipeableDrawerBookingSummary,
		openDrawerState,
		setOpenDrawerState,
		handleChangeModals,
		handleNext,
		isDesktop,
		shouldUseNewResumeLayout,
	};
}
